import { memoize } from "@solvari/utils";

import "./fontAwesome.css";

const token = "51b4c236cf";

const getUrl = (kitUrlName: string) => (iconUrlName: string) => {
  return `https://ka-p.fontawesome.com/releases/v6.7.1/svgs/${kitUrlName}/${iconUrlName}.svg?token=${token}`;
};

function getCustomUrl(iconUrlName: string) {
  return `https://kit-uploads.fontawesome.com/${token}/icons/${iconUrlName}-1.svg?token=${token}`;
}

const packages = {
  /* eslint-disable @typescript-eslint/naming-convention */
  "fa-solid": { alternates: ["fas", "fa"], getUrl: getUrl("solid") },
  "fa-regular": { alternates: ["far"], getUrl: getUrl("regular") },
  "fa-light": { alternates: ["fal"], getUrl: getUrl("light") },
  "fa-duotone": { alternates: ["fad"], getUrl: getUrl("duotone") },
  "fa-thin": { alternates: ["fat"], getUrl: getUrl("thin") },
  "fa-brands": { alternates: ["fab"], getUrl: getUrl("brands") },
  "fa-kit": { alternates: ["fak"], getUrl: getCustomUrl },
  /* eslint-enable @typescript-eslint/naming-convention */
} as const;

type PackageLong = keyof typeof packages;
type PackageAlternate = (typeof packages)[PackageLong]["alternates"][number];

const packageAlternates = Object.fromEntries(
  Object.entries(packages).flatMap(([longName, { alternates }]) =>
    alternates.map((alternate) => [alternate, longName] as const),
  ),
) as Record<PackageAlternate, PackageLong>;

const packageSelector = `.${[...Object.keys(packages), ...Object.keys(packageAlternates)].join(",.")}`;

function getIconPackage(classList: string[]) {
  const longNameClass = classList.find(
    (className): className is PackageLong => className in packages,
  );

  const alternateNameClass = classList.find(
    (className): className is PackageAlternate =>
      className in packageAlternates,
  );

  if (!longNameClass && !alternateNameClass) {
    return;
  }

  const longName = longNameClass ?? packageAlternates[alternateNameClass!];

  return {
    longName,
    classNames: [longName, ...packages[longName].alternates],
    getUrl: packages[longName].getUrl,
  };
}

type IconData = {
  css: string;
  iconName: string;
  iconUrl: string;
};

const getIconDataFromClassList = memoize(
  (classList: string[]): IconData | undefined => {
    const iconPackage = getIconPackage(classList);

    if (!iconPackage) {
      return;
    }

    const iconName = classList.find((className) => {
      return className.startsWith("fa-") && className !== iconPackage.longName;
    });

    if (!iconName) {
      return;
    }
    const iconUrlName = iconName.replace("fa-", "");
    const iconUrl = iconPackage.getUrl(iconUrlName);

    const selector = iconPackage.classNames
      .map((className) => `.${className}.${iconName}`)
      .join(",");

    const css = `${selector} { background-color: currentColor; --fa-icon-url: url(${iconUrl}) }`;

    return { iconName, iconUrl, css };
  },
);

function getStyleElement() {
  const existingEl = document.querySelector<HTMLStyleElement>(
    "style#s-font-awesome",
  );
  if (!existingEl) {
    document.head.insertAdjacentHTML(
      "beforeend",
      "<style id='s-font-awesome'></style>",
    );
  }
  return document.querySelector<HTMLStyleElement>("style#s-font-awesome")!;
}

function getFontAwesomeIconData(document = window.document.body) {
  const iconElements = [
    ...document.querySelectorAll<HTMLElement>(packageSelector),
  ];

  const iconMap = iconElements.reduce<Map<string, IconData>>((icons, elem) => {
    const faClassList = [...elem.classList.values()].filter((className) =>
      className.startsWith("fa"),
    );
    const data = getIconDataFromClassList(faClassList);
    if (data && !icons.has(data.iconUrl)) {
      icons.set(data.iconUrl, data);
    }
    return icons;
  }, new Map<string, IconData>());

  return [...iconMap.values()].toSorted((iconA, iconB) =>
    iconA.css.localeCompare(iconB.css),
  );
}

function iconDataToCss(iconData: IconData[]) {
  return iconData.map(({ css }) => css).join("");
}

function applyFontAwesomeIconCss() {
  const cssString = iconDataToCss(getFontAwesomeIconData());
  const styleElement = getStyleElement();

  if (styleElement.innerHTML !== cssString) {
    styleElement.innerHTML = cssString;
  }
}

function initFontAwesome() {
  applyFontAwesomeIconCss();
  const observer = new MutationObserver(() => applyFontAwesomeIconCss());
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributeFilter: ["class"],
  });
}

export { getFontAwesomeIconData, iconDataToCss, initFontAwesome };
